import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {formatDateTime} from '../shared/DocumentApi'
import {getCallPoolEntry, loadCallJobOptions, markCallNegative, markCallPositive} from './Api'
import {ICallJobOption, ICallPoolEntryModel, NEGATIVE_REASONS} from './CallPoolModel'

type Props = {
  entryId: string
  /** Sonuç kaydedilince (aday arama listesinden düşer) */
  onDone: (message: string) => void
}

/**
 * Künyede arama havuzu kaydı için Olumlu / Olumsuz.
 * Olumlu: uygun ilanlar seçilir, ücret beklentisi girilir; her ilan için ayrı süreç başlar.
 * Olumsuz: sebep ve not; adayın başvuruları olumsuz sonuçlanır.
 */
export const CallResultPanel: FC<Props> = ({entryId, onDone}) => {
  const [entry, setEntry] = useState<ICallPoolEntryModel | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [positiveOpen, setPositiveOpen] = useState(false)
  const [negativeOpen, setNegativeOpen] = useState(false)

  useEffect(() => {
    setEntry(null)
    setError(null)
    getCallPoolEntry(entryId)
      .then(({data}) => setEntry(data.payload))
      .catch((err) => setError(apiErrorMessage(err, 'Arama kaydı bulunamadı')))
  }, [entryId])

  if (error) {
    return <div className='alert alert-warning mb-6'>{error}</div>
  }
  if (!entry) return null

  return (
    <>
      <div className='card mb-6 border border-dashed border-success'>
        <div className='card-body d-flex flex-wrap align-items-center justify-content-between gap-4 py-5'>
          <div className='d-flex align-items-center gap-4'>
            <i className='bi bi-telephone-outbound fs-2x text-success'></i>
            <div className='d-flex flex-column'>
              <span className='fw-bolder fs-5 text-gray-800'>Arama sonucu</span>
              <span className='text-muted fs-7'>
                {entry.jobTitle ? `Başvuru: ${entry.jobTitle} · ` : 'Aday havuzundan · '}
                {formatDateTime(entry.createdDate)} tarihinde sana atandı
              </span>
            </div>
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-success' onClick={() => setPositiveOpen(true)}>
              <i className='bi bi-hand-thumbs-up'></i>
              Olumlu
            </button>
            <button type='button' className='btn btn-danger' onClick={() => setNegativeOpen(true)}>
              <i className='bi bi-hand-thumbs-down'></i>
              Olumsuz
            </button>
          </div>
        </div>
      </div>

      <PositiveCallModal
        show={positiveOpen}
        entryId={entry.id}
        onClose={() => setPositiveOpen(false)}
        onDone={(count) => {
          setPositiveOpen(false)
          onDone(`${count} ilan için süreç başlatıldı; aday Aday Takip ekranına eklendi`)
        }}
      />
      <NegativeCallModal
        show={negativeOpen}
        entryId={entry.id}
        onClose={() => setNegativeOpen(false)}
        onDone={() => {
          setNegativeOpen(false)
          onDone('Olumsuz arama kaydedildi')
        }}
      />
    </>
  )
}

const PositiveCallModal: FC<{
  show: boolean
  entryId: string
  onClose: () => void
  onDone: (count: number) => void
}> = ({show, entryId, onClose, onDone}) => {
  const [options, setOptions] = useState<ICallJobOption[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [salary, setSalary] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!show) return
    setSelected([])
    setSalary('')
    setNote('')
    setSubmitted(false)
    setLoading(true)
    loadCallJobOptions(entryId)
      .then(({data}) => setOptions(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'İlanlar yüklenemedi')))
      .finally(() => setLoading(false))
  }, [show, entryId])

  const selectable = (option: ICallJobOption) => option.hasProcess && !option.trackingStarted

  const toggle = (jobId: string) =>
    setSelected((ids) => (ids.includes(jobId) ? ids.filter((id) => id !== jobId) : [...ids, jobId]))

  const handleSubmit = async () => {
    setSubmitted(true)
    if (selected.length === 0 || !salary.trim()) return
    setSaving(true)
    try {
      await markCallPositive(entryId, selected, salary.trim(), note.trim())
      onDone(selected.length)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Süreç başlatılamadı'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered' backdrop='static'>
      <div className='modal-header'>
        <h5 className='modal-title'>Olumlu · Uygun İlanlar</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        <div className='text-muted fs-7 mb-4'>
          Adaya uygun gördüğün ilanları seç. Her ilan için ayrı süreç başlatılır; aday başvurmadığı ilanlar için
          adına başvuru açılır.
        </div>

        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-primary' />
          </div>
        ) : options.length === 0 ? (
          <div className='text-muted text-center py-8'>Yayında ilan bulunamadı.</div>
        ) : (
          <div className='d-flex flex-column gap-2 mb-6' style={{maxHeight: 340, overflowY: 'auto'}}>
            {options.map((option) => {
              const enabled = selectable(option)
              return (
                <label
                  key={option.jobId}
                  className={`d-flex align-items-start gap-3 border rounded p-4 ${
                    selected.includes(option.jobId) ? 'border-primary bg-light-primary' : 'border-gray-300'
                  } ${enabled ? 'cursor-pointer' : 'opacity-50'}`}
                >
                  <input
                    type='checkbox'
                    className='form-check-input mt-1'
                    disabled={!enabled}
                    checked={selected.includes(option.jobId)}
                    onChange={() => toggle(option.jobId)}
                  />
                  <span className='d-flex flex-column flex-grow-1'>
                    <span className='d-flex flex-wrap align-items-center gap-2'>
                      <span className='fw-bold text-gray-800'>{option.title}</span>
                      {option.applied && <span className='badge badge-success'>Başvurdu</span>}
                      {option.trackingStarted && <span className='badge badge-light-secondary'>Süreç başlatılmış</span>}
                      {!option.hasProcess && <span className='badge badge-light-warning'>Süreç bağlı değil</span>}
                    </span>
                    {(option.subTitle || option.locations) && (
                      <span className='text-muted fs-7'>
                        {[option.subTitle, option.locations].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
        )}
        {submitted && selected.length === 0 && <div className='text-danger fs-7 mb-4'>En az bir ilan seçiniz</div>}

        <div className='row g-5'>
          <div className='col-md-5'>
            <label className='form-label fw-bold required'>Ücret Beklentisi</label>
            <input
              type='text'
              maxLength={100}
              className={`form-control ${submitted && !salary.trim() ? 'is-invalid' : ''}`}
              placeholder='Örn: 60.000 TL net'
              value={salary}
              onChange={(event) => setSalary(event.target.value)}
            />
            {submitted && !salary.trim() && <div className='invalid-feedback'>Ücret beklentisi zorunludur</div>}
          </div>
          <div className='col-md-7'>
            <label className='form-label fw-bold'>Not</label>
            <textarea
              rows={2}
              maxLength={5000}
              className='form-control'
              placeholder='Görüşme notu'
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          İptal
        </button>
        <button type='button' className='btn btn-success' disabled={saving} onClick={handleSubmit}>
          {saving ? (
            <span className='spinner-border spinner-border-sm' />
          ) : (
            `Süreç Başlat${selected.length > 0 ? ` (${selected.length})` : ''}`
          )}
        </button>
      </div>
    </Modal>
  )
}

const NegativeCallModal: FC<{
  show: boolean
  entryId: string
  onClose: () => void
  onDone: () => void
}> = ({show, entryId, onClose, onDone}) => {
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    setReason('')
    setNote('')
  }, [show])

  const handleSubmit = async () => {
    if (!reason) return
    setSaving(true)
    try {
      await markCallNegative(entryId, reason, note.trim())
      onDone()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kaydedilemedi'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} dialogClassName='modal-dialog-centered' backdrop='static'>
      <div className='modal-header'>
        <h5 className='modal-title'>Olumsuz Arama</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        <label className='form-label fw-bold required'>Sebep</label>
        <div className='d-flex flex-column gap-2 mb-5'>
          {NEGATIVE_REASONS.map((option) => (
            <label key={option.value} className='form-check form-check-custom form-check-solid'>
              <input
                type='radio'
                className='form-check-input'
                name='negative-reason'
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
              />
              <span className='form-check-label text-gray-800'>{option.label}</span>
            </label>
          ))}
        </div>
        <label className='form-label fw-bold'>Not</label>
        <textarea
          rows={3}
          maxLength={5000}
          className='form-control'
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className='form-text mt-3'>
          Adayın başvurusu olumsuz olarak güncellenir; devam eden süreci varsa olumsuz tamamlanır.
        </div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          İptal
        </button>
        <button type='button' className='btn btn-danger' disabled={!reason || saving} onClick={handleSubmit}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : 'Olumsuz Kaydet'}
        </button>
      </div>
    </Modal>
  )
}
